import './CreateBook.css';
import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { fetchBookById, fetchSimplifiedBookById, updateBook } from '../../services/bookService';
import { searchAuthors, fetchAuthorById } from '../../services/authorService';
import { BOOK_TYPES, CHARACTER_ROLES } from '../../constants/bookConstants';

function EditBook() {
    const navigate = useNavigate();
    const { id } = useParams();

    const [formData, setFormData] = useState({
        bookTitle: '',
        shortSummary: '',
        summary: '',
        detailedPlot: '',
        bookType: '',
        genres: [],
        publicationYear: '',
        countryOfOrigin: '',
        historyEra: '',
        culturalContext: '',
        recognition: '',
        otherInfo: ''
    });
    const [characters, setCharacters] = useState([]);
    const [currentCharacter, setCurrentCharacter] = useState({
        name: '',
        info: '',
        characterType: ''
    });
    const [editingCharacterIndex, setEditingCharacterIndex] = useState(null);
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState(null);
    const [genreInput, setGenreInput] = useState('');

    // Author search state
    const [authorSearchTerm, setAuthorSearchTerm] = useState('');
    const [debouncedAuthorSearchTerm, setDebouncedAuthorSearchTerm] = useState('');
    const [authorSearchResults, setAuthorSearchResults] = useState([]);
    const [selectedAuthor, setSelectedAuthor] = useState(null);
    const [authorSearching, setAuthorSearching] = useState(false);

    // Scroll to top when error is set
    useEffect(() => {
        if (error) {
            window.scrollTo({ top: 0, behavior: 'smooth' });
        }
    }, [error]);

    // Load book data
    const loadBook = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);

            // Fetch both full and simplified book data in parallel
            const [bookData, simplifiedData] = await Promise.all([
                fetchBookById(id),
                fetchSimplifiedBookById(id)
            ]);

            setFormData({
                bookTitle: bookData.bookTitle || '',
                shortSummary: simplifiedData.shortSummary || '',
                summary: bookData.summary || '',
                detailedPlot: bookData.detailedPlot || '',
                bookType: bookData.bookType || '',
                genres: bookData.genres || [],
                publicationYear: bookData.publicationYear || '',
                countryOfOrigin: bookData.countryOfOrigin || '',
                historyEra: bookData.historyEra || '',
                culturalContext: bookData.culturalContext || '',
                recognition: bookData.recognition || '',
                otherInfo: bookData.otherInfo || ''
            });

            setCharacters(bookData.characters || []);

            // Load author - the simplified data always has the author object
            if (simplifiedData.author) {
                setSelectedAuthor(simplifiedData.author);
            } else if (bookData.authorId) {
                // Fallback: If only authorId is provided in full data, fetch the author
                try {
                    const authorData = await fetchAuthorById(bookData.authorId);
                    setSelectedAuthor(authorData);
                } catch (err) {
                    console.error('Error loading author:', err);
                    setError(`Nepodařilo se načíst autora (ID: ${bookData.authorId}). ${err.message}`);
                }
            } else {
                console.warn('No author or authorId found in book data');
            }
        } catch (err) {
            setError('Nepodařilo se načíst knihu. Zkuste to prosím znovu.');
            console.error('Error loading book:', err);
        } finally {
            setLoading(false);
        }
    }, [id]);

    useEffect(() => {
        loadBook();
    }, [loadBook]);

    // Debounce author search term
    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedAuthorSearchTerm(authorSearchTerm);
        }, 500);

        return () => clearTimeout(timer);
    }, [authorSearchTerm]);

    // Search for authors when debounced term changes
    useEffect(() => {
        const performSearch = async () => {
            if (!debouncedAuthorSearchTerm.trim()) {
                setAuthorSearchResults([]);
                return;
            }

            try {
                setAuthorSearching(true);
                const results = await searchAuthors(debouncedAuthorSearchTerm.trim(), 0, 5);
                setAuthorSearchResults(results.content);
            } catch (err) {
                console.error('Error searching authors:', err);
                setAuthorSearchResults([]);
            } finally {
                setAuthorSearching(false);
            }
        };

        performSearch();
    }, [debouncedAuthorSearchTerm]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleGenreAdd = (genre) => {
        if (!formData.genres.includes(genre)) {
            setFormData(prev => ({
                ...prev,
                genres: [...prev.genres, genre]
            }));
        }
    };

    const handleGenreRemove = (genreToRemove) => {
        setFormData(prev => ({
            ...prev,
            genres: prev.genres.filter(g => g !== genreToRemove)
        }));
    };

    const handleCharacterChange = (e) => {
        const { name, value } = e.target;
        setCurrentCharacter(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleAddCharacter = () => {
        if (!currentCharacter.name.trim()) {
            setError('Jméno postavy je povinné');
            return;
        }
        if (!currentCharacter.characterType) {
            setError('Role postavy je povinná');
            return;
        }

        if (editingCharacterIndex !== null) {
            // Update existing character
            setCharacters(prev => prev.map((char, index) =>
                index === editingCharacterIndex ? { ...currentCharacter } : char
            ));
            setEditingCharacterIndex(null);
        } else {
            // Add new character
            setCharacters(prev => [...prev, { ...currentCharacter }]);
        }

        setCurrentCharacter({
            name: '',
            info: '',
            characterType: ''
        });
        setError(null);
    };

    const handleEditCharacter = (index) => {
        setCurrentCharacter({ ...characters[index] });
        setEditingCharacterIndex(index);
    };

    const handleCancelEditCharacter = () => {
        setCurrentCharacter({
            name: '',
            info: '',
            characterType: ''
        });
        setEditingCharacterIndex(null);
    };

    const handleRemoveCharacter = (index) => {
        setCharacters(prev => prev.filter((_, i) => i !== index));
        // If we're editing the character being removed, cancel the edit
        if (editingCharacterIndex === index) {
            handleCancelEditCharacter();
        } else if (editingCharacterIndex !== null && editingCharacterIndex > index) {
            // Adjust the editing index if a character before it was removed
            setEditingCharacterIndex(editingCharacterIndex - 1);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError(null);

        // Validation
        if (!formData.bookTitle.trim()) {
            setError('Název knihy je povinný');
            return;
        }
        if (!selectedAuthor) {
            setError('Autor je povinný');
            return;
        }
        if (!formData.summary.trim()) {
            setError('Shrnutí a hlavní myšlenky jsou povinné');
            return;
        }
        if (!formData.bookType) {
            setError('Typ knihy je povinný');
            return;
        }
        if (!formData.genres || formData.genres.length === 0) {
            setError('Vyberte alespoň jeden žánr');
            return;
        }

        try {
            setSubmitting(true);

            const bookData = {
                ...formData,
                authorId: selectedAuthor.id,
                characters: characters
            };

            await updateBook(id, bookData);

            // Navigate immediately with success message
            navigate('/admin/manage-books', {
                state: { message: 'Kniha byla úspěšně aktualizována!' }
            });
        } catch (err) {
            setError('Nepodařilo se aktualizovat knihu. Zkuste to prosím znovu.');
            console.error('Error updating book:', err);
            setSubmitting(false);
        }
    };

    const handleAuthorSelect = (author) => {
        setSelectedAuthor(author);
        setAuthorSearchTerm('');
        setAuthorSearchResults([]);
    };

    const handleClearAuthor = () => {
        setSelectedAuthor(null);
    };

    const handleBack = () => {
        navigate('/admin/manage-books');
    };

    if (loading) {
        return (
            <div className="admin-page">
                <div className="form-container">
                    <div className="loading-container">
                        <p>Načítání knihy...</p>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="admin-page">
            <div className="form-container">
                <button className="back-button" onClick={handleBack}>
                    ← Zpět na seznam knih
                </button>

                <h1 className="page-title">Upravit knihu</h1>

                {error && (
                    <div className="alert alert-error">
                        {error}
                    </div>
                )}

                <form onSubmit={handleSubmit} className="create-form">
                    {/* Book Title */}
                    <div className="form-group">
                        <label htmlFor="bookTitle" className="form-label">
                            Název knihy <span className="required">*</span>
                        </label>
                        <input
                            type="text"
                            id="bookTitle"
                            name="bookTitle"
                            className="form-input"
                            value={formData.bookTitle}
                            onChange={handleInputChange}
                            placeholder="Např. Babička"
                            disabled={submitting}
                        />
                    </div>

                    {/* Publication Year */}
                    <div className="form-group">
                        <label htmlFor="publicationYear" className="form-label">
                            Rok vydání
                        </label>
                        <input
                            type="text"
                            id="publicationYear"
                            name="publicationYear"
                            className="form-input"
                            value={formData.publicationYear}
                            onChange={handleInputChange}
                            placeholder="např. 1855, 19. století, 1850-1860"
                            disabled={submitting}
                        />
                    </div>

                    {/* Country of Origin */}
                    <div className="form-group">
                        <label htmlFor="countryOfOrigin" className="form-label">
                            Země původu
                        </label>
                        <input
                            type="text"
                            id="countryOfOrigin"
                            name="countryOfOrigin"
                            className="form-input"
                            value={formData.countryOfOrigin}
                            onChange={handleInputChange}
                            placeholder="např. Česko, Francie, Rusko (nepovinné)"
                            disabled={submitting}
                        />
                    </div>

                    {/* Short Summary */}
                    <div className="form-group">
                        <label htmlFor="shortSummary" className="form-label">
                            Krátké shrnutí
                        </label>
                        <textarea
                            id="shortSummary"
                            name="shortSummary"
                            className="form-textarea"
                            value={formData.shortSummary}
                            onChange={handleInputChange}
                            placeholder="Stručné shrnutí pro seznam knih (1-2 věty)..."
                            rows="5"
                            disabled={submitting}
                        />
                    </div>

                    {/* Author Search */}
                    <div className="form-group">
                        <label htmlFor="authorSearch" className="form-label">
                            Autor <span className="required">*</span>
                        </label>

                        {selectedAuthor ? (
                            <div className="selected-author">
                                <div className="selected-author-info">
                                    <span className="selected-author-name">{selectedAuthor.name}</span>
                                </div>
                                <button
                                    type="button"
                                    className="btn-clear-author"
                                    onClick={handleClearAuthor}
                                    disabled={submitting}
                                    title="Zrušit výběr autora"
                                >
                                    ✕
                                </button>
                            </div>
                        ) : (
                            <div className="author-search-container">
                                <input
                                    type="text"
                                    id="authorSearch"
                                    className="form-input"
                                    value={authorSearchTerm}
                                    onChange={(e) => setAuthorSearchTerm(e.target.value)}
                                    placeholder="Vyhledat autora podle jména..."
                                    disabled={submitting}
                                />

                                {authorSearching && (
                                    <div className="author-search-loading">
                                        Vyhledávání...
                                    </div>
                                )}

                                {!authorSearching && authorSearchResults.length > 0 && (
                                    <div className="author-search-results">
                                        {authorSearchResults.map(author => (
                                            <div
                                                key={author.id}
                                                className="author-search-result-item"
                                                onClick={() => handleAuthorSelect(author)}
                                            >
                                                <span className="author-result-name">{author.name}</span>
                                            </div>
                                        ))}
                                    </div>
                                )}

                                {!authorSearching && authorSearchTerm && debouncedAuthorSearchTerm && authorSearchResults.length === 0 && (
                                    <div className="author-search-no-results">
                                        Žádní autoři nenalezeni
                                    </div>
                                )}
                            </div>
                        )}
                    </div>

                    {/* Book Type */}
                    <div className="form-group">
                        <label htmlFor="bookType" className="form-label">
                            Typ knihy <span className="required">*</span>
                        </label>
                        <select
                            id="bookType"
                            name="bookType"
                            className="form-select"
                            value={formData.bookType}
                            onChange={handleInputChange}
                            disabled={submitting}
                        >
                            <option value="">Vyberte typ</option>
                            {BOOK_TYPES.map(type => (
                                <option key={type.value} value={type.value}>
                                    {type.label}
                                </option>
                            ))}
                        </select>
                    </div>

                    {/* Genre */}
                    <div className="form-group">
                        <label className="form-label">
                            Žánry <span className="required">*</span>
                        </label>

                        {/* Genre Input */}
                        <div className="genre-input-row">
                            <input
                                type="text"
                                className="form-input"
                                value={genreInput}
                                onChange={(e) => setGenreInput(e.target.value)}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter') {
                                        e.preventDefault();
                                        const trimmed = genreInput.trim();
                                        if (trimmed) { handleGenreAdd(trimmed); setGenreInput(''); }
                                    }
                                }}
                                placeholder="Zadejte žánr a stiskněte Enter nebo Přidat"
                                disabled={submitting}
                            />
                            <button
                                type="button"
                                className="genre-add-btn"
                                onClick={() => {
                                    const trimmed = genreInput.trim();
                                    if (trimmed) { handleGenreAdd(trimmed); setGenreInput(''); }
                                }}
                                disabled={submitting || !genreInput.trim()}
                            >
                                Přidat
                            </button>
                        </div>

                        {/* Selected Genres Tags */}
                        {formData.genres.length > 0 && (
                            <div className="selected-genres-tags">
                                {formData.genres.map(genre => (
                                    <div key={genre} className="genre-tag">
                                        <span className="genre-tag-text">{genre}</span>
                                        <button
                                            type="button"
                                            className="genre-tag-remove"
                                            onClick={() => handleGenreRemove(genre)}
                                            disabled={submitting}
                                            title="Odstranit žánr"
                                        >
                                            ✕
                                        </button>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    {/* Summary */}
                    <div className="form-group">
                        <label htmlFor="summary" className="form-label">
                            Shrnutí a hlavní myšlenky <span className="required">*</span>
                        </label>
                        <textarea
                            id="summary"
                            name="summary"
                            className="form-textarea"
                            value={formData.summary}
                            onChange={handleInputChange}
                            placeholder="Shrnutí děje, hlavní témata, myšlenky a inspirace díla..."
                            rows="10"
                            disabled={submitting}
                        />
                    </div>

                    {/* Detailed Plot */}
                    <div className="form-group">
                        <label htmlFor="detailedPlot" className="form-label">
                            Podrobný děj
                        </label>
                        <textarea
                            id="detailedPlot"
                            name="detailedPlot"
                            className="form-textarea"
                            value={formData.detailedPlot}
                            onChange={handleInputChange}
                            placeholder="Detailní popis děje knihy..."
                            rows="15"
                            disabled={submitting}
                        />
                    </div>

                    {/* Characters Section */}
                    <div className="form-group">
                        <label className="form-label">Postavy</label>

                        <div className="character-input-section">
                            <div className="character-input-row">
                                <input
                                    type="text"
                                    name="name"
                                    className="form-input character-name-input"
                                    value={currentCharacter.name}
                                    onChange={handleCharacterChange}
                                    placeholder="Jméno postavy"
                                    disabled={submitting}
                                />
                                <select
                                    name="characterType"
                                    className="form-select character-role-select"
                                    value={currentCharacter.characterType}
                                    onChange={handleCharacterChange}
                                    disabled={submitting}
                                >
                                    <option value="">Vyberte roli</option>
                                    {CHARACTER_ROLES.map(role => (
                                        <option key={role.value} value={role.value}>
                                            {role.label}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            <textarea
                                name="info"
                                className="form-textarea character-description-textarea"
                                value={currentCharacter.info}
                                onChange={handleCharacterChange}
                                placeholder="Popis postavy..."
                                rows="6"
                                disabled={submitting}
                            />
                            <div className="character-input-actions">
                                <button
                                    type="button"
                                    className="btn btn-secondary add-character-btn"
                                    onClick={handleAddCharacter}
                                    disabled={submitting}
                                >
                                    {editingCharacterIndex !== null ? '✓ Uložit změny' : '+ Přidat postavu'}
                                </button>
                                {editingCharacterIndex !== null && (
                                    <button
                                        type="button"
                                        className="btn btn-secondary cancel-edit-btn"
                                        onClick={handleCancelEditCharacter}
                                        disabled={submitting}
                                    >
                                        ✕ Zrušit
                                    </button>
                                )}
                            </div>
                        </div>

                        {characters.length > 0 && (
                            <div className="characters-list">
                                <h3 className="characters-list-title">Přidané postavy:</h3>
                                {characters.map((char, index) => (
                                    <div
                                        key={index}
                                        className={`character-item ${editingCharacterIndex === index ? 'editing' : ''}`}
                                    >
                                        <div className="character-item-header">
                                            <span className="character-item-name">{char.name}</span>
                                            <span className="character-item-role">
                                                ({CHARACTER_ROLES.find(r => r.value === char.characterType)?.label})
                                            </span>
                                            <div className="character-item-actions">
                                                <button
                                                    type="button"
                                                    className="btn-edit-character"
                                                    onClick={() => handleEditCharacter(index)}
                                                    disabled={submitting || editingCharacterIndex !== null}
                                                    title="Upravit postavu"
                                                >
                                                    ✏️
                                                </button>
                                                <button
                                                    type="button"
                                                    className="btn-remove-character"
                                                    onClick={() => handleRemoveCharacter(index)}
                                                    disabled={submitting}
                                                    title="Odstranit postavu"
                                                >
                                                    ✕
                                                </button>
                                            </div>
                                        </div>
                                        {char.info && (
                                            <p className="character-item-description">{char.info}</p>
                                        )}
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    {/* History Era */}
                    <div className="form-group">
                        <label htmlFor="historyEra" className="form-label">
                            Historická éra
                        </label>
                        <textarea
                            id="historyEra"
                            name="historyEra"
                            className="form-textarea"
                            value={formData.historyEra}
                            onChange={handleInputChange}
                            placeholder="Např. 19. století, meziválečné období"
                            rows="8"
                            disabled={submitting}
                        />
                    </div>

                    {/* Cultural Context */}
                    <div className="form-group">
                        <label htmlFor="culturalContext" className="form-label">
                            Kulturní kontext
                        </label>
                        <textarea
                            id="culturalContext"
                            name="culturalContext"
                            className="form-textarea"
                            value={formData.culturalContext}
                            onChange={handleInputChange}
                            placeholder="Např. český realismus, romantismus"
                            rows="8"
                            disabled={submitting}
                        />
                    </div>

                    {/* Recognition */}
                    <div className="form-group">
                        <label htmlFor="recognition" className="form-label">
                            Přijetí společností
                        </label>
                        <textarea
                            id="recognition"
                            name="recognition"
                            className="form-textarea"
                            value={formData.recognition}
                            onChange={handleInputChange}
                            placeholder="Jak dílo přijala společnost v době vydání a později, jeho vliv a význam"
                            rows="8"
                            disabled={submitting}
                        />
                    </div>

                    {/* Other Info */}
                    <div className="form-group">
                        <label htmlFor="otherInfo" className="form-label">
                            Další informace
                        </label>
                        <textarea
                            id="otherInfo"
                            name="otherInfo"
                            className="form-textarea"
                            value={formData.otherInfo}
                            onChange={handleInputChange}
                            placeholder="Jakékoliv další poznámky..."
                            rows="8"
                            disabled={submitting}
                        />
                    </div>

                    {/* Submit Button */}
                    <div className="form-actions">
                        <button
                            type="button"
                            className="btn btn-secondary"
                            onClick={handleBack}
                            disabled={submitting}
                        >
                            Zrušit
                        </button>
                        <button
                            type="submit"
                            className="btn btn-primary"
                            disabled={submitting}
                        >
                            {submitting && <span className="btn-spinner"></span>}
                            {submitting ? 'Ukládání...' : 'Uložit změny'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}

export default EditBook;
